import React, { useCallback, useEffect, useState } from "react";
import { Bell, BellRing, Bot, RefreshCw, Zap } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { EmptyState, Panel, StatusChip } from "../ui";

export const AutomationSection = () => {
  const { request } = useStudioAuth();
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [statusResponse, logResponse] = await Promise.all([
      request("/operations/alerts/status"),
      request("/operations/alerts/log"),
    ]);
    if (statusResponse.ok) setStatus(await statusResponse.json());
    if (logResponse.ok) setLog(await logResponse.json());
  }, [request]);

  useEffect(() => { load(); const timer = setInterval(load, 15000); return () => clearInterval(timer); }, [load]);

  const patch = async (body, message) => {
    const response = await request("/operations/alerts/settings", { method: "POST", body: JSON.stringify(body) });
    if (!response.ok) return toast.error("Setting could not be saved");
    setStatus(await response.json().then((data) => ({ ...status, ...data })));
    toast.success(message);
    load();
  };

  const pollNow = async () => {
    setBusy(true);
    const response = await request("/operations/alerts/poll", { method: "POST" });
    setBusy(false);
    if (!response.ok) return toast.error("Poll failed");
    const result = await response.json();
    toast.success(`${result.warnings} warnings scanned · ${result.new} new · ${result.staged} staged · ${result.pushed} pushed`);
    load();
  };

  const testPush = async () => {
    const response = await request("/operations/alerts/test-push", { method: "POST" });
    if (!response.ok) return toast.error("Test push failed");
    const result = await response.json();
    toast.success(`Test push sent to ${result.sent}/${result.subscribers} devices`);
  };

  const row = "flex items-center justify-between gap-4 border-b border-[rgba(120,170,255,0.12)] px-4 py-3 last:border-b-0";

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_340px]">
      <Panel
        title="Auto Storm Prep"
        icon={Bot}
        note="New NWS warnings stage into PREVIEW only — never straight to air"
        actions={
          <>
            <StatusChip state={status?.running ? "ok" : "error"} testId="automation-engine-status">{status?.running ? "Engine running" : "Engine stopped"}</StatusChip>
            <button data-testid="automation-refresh-button" onClick={load} title="Refresh" className="grid h-7 w-7 place-items-center border border-white/10 text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </>
        }
        bodyClass="p-0"
      >
        <div className={row}>
          <div>
            <div className="text-sm font-bold text-white">Stage warnings automatically</div>
            <div className="mono mt-1 text-[9px] uppercase tracking-widest text-slate-600">All warnings · United States</div>
          </div>
          <Switch
            data-testid="automation-autoprep-switch"
            checked={Boolean(status?.auto_prep_enabled)}
            onCheckedChange={(checked) => patch({ auto_prep_enabled: checked }, checked ? "Auto storm prep on" : "Auto storm prep off")}
          />
        </div>
        <div className={row}>
          <div>
            <div className="text-sm font-bold text-white">Viewer push alerts</div>
            <div className="mono mt-1 text-[9px] uppercase tracking-widest text-slate-600">Sent to each viewer's own state · max 12/hour</div>
          </div>
          <Switch
            data-testid="automation-push-switch"
            checked={Boolean(status?.push_enabled)}
            onCheckedChange={(checked) => patch({ push_enabled: checked }, checked ? "Viewer push alerts on" : "Viewer push alerts off")}
          />
        </div>
        <div className="grid grid-cols-3 gap-px bg-[rgba(120,170,255,0.12)]">
          {[
            { label: "Warnings tracked", value: status?.tracked_warnings ?? "—" },
            { label: "Staged today", value: status?.staged_today ?? "—" },
            { label: "Subscribers", value: status?.subscribers ?? "—" },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#0a0f1d] px-4 py-3">
              <div className="mono text-[9px] uppercase tracking-widest text-slate-600">{stat.label}</div>
              <div className="display mt-1 text-2xl font-black tabular-nums text-white">{stat.value}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 p-4">
          <Button data-testid="automation-poll-now-button" onClick={pollNow} disabled={busy} className="h-9 rounded-none bg-[#1a5cff] text-[10px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]">
            <Zap className="h-4 w-4" /> {busy ? "Scanning" : "Scan NWS now"}
          </Button>
          <Button data-testid="automation-test-push-button" onClick={testPush} variant="outline" className="h-9 rounded-none border-white/15 bg-white/[0.03] text-[10px] font-bold uppercase tracking-widest text-slate-100 hover:bg-white/[0.08]">
            <BellRing className="h-4 w-4" /> Send test push
          </Button>
        </div>
        {status?.last_error && (
          <p className="mono border-t border-amber-400/20 bg-amber-400/[0.06] px-4 py-2 text-[10px] uppercase tracking-widest text-amber-200">Last error: {status.last_error}</p>
        )}
      </Panel>

      <Panel title="Recently staged" icon={Bell} note={status?.last_poll ? `Last scan ${new Date(status.last_poll).toLocaleTimeString()}` : ""} bodyClass="p-0">
        {log.length === 0 ? (
          <EmptyState icon={Bell} title="Nothing staged yet" message="The next new NWS warning will appear here and load into PREVIEW automatically." testId="automation-log-empty" />
        ) : (
          <div className="divide-y divide-[rgba(120,170,255,0.12)]">
            {log.map((entry) => (
              <div key={entry.alert_id} data-testid={`automation-log-${entry.alert_id}`} className="px-4 py-3">
                <div className="text-xs font-bold uppercase text-white">{entry.event}</div>
                <div className="mt-1 line-clamp-2 text-[11px] text-slate-400">{entry.area}</div>
                <div className="mono mt-1 text-[9px] uppercase tracking-widest text-slate-600">{new Date(entry.staged_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default AutomationSection;
